import React, { useEffect } from 'react';
import { UnorderedList } from '@Components/unordered_list/view';
import s from '../styles/Buying.module.css';
import tabPanel from '../styles/TabPanel.module.css';
import { CourseProps } from '../props';
import axios from 'axios';
import { cleanCourseName } from '@Util/util';
import { parseSanitizedHtml } from '@Util/sanitize';
import Heading2 from '@Components/heading_2/view';
import Heading3 from '@Components/heading_3/view';

type BuyingProps = {
    show: boolean;
    children: React.ReactNode;
    courseName: string;
    course: CourseProps;
    courseId: number;
};
export const Buying = (props: BuyingProps) => {
    const [flows, setFlows] = React.useState([]);
    const [materials, setMaterials] = React.useState([]);
    const [corporateMemberDocuments, setCorporateMemberDocuments] = React.useState([]);
    const [individualDocuments, setIndividualDocuments] = React.useState([]);
    const [allTargetDocuments, setAllTargetDocuments] = React.useState([]);
    const displayDocuments =
        corporateMemberDocuments.length + individualDocuments.length + allTargetDocuments.length > 0;

    // 非アクティブなタブもDOMに残すため、表示前からマウントされる。
    // 初期表示で不要なリクエストを出さないよう、最初に表示されたときだけ取得する。
    // 失敗した場合はフラグを戻し、次にタブを開いたときに再取得できるようにする
    const hasFetched = React.useRef(false);

    useEffect(() => {
        if (!props.show || hasFetched.current) return;
        hasFetched.current = true;

        const onError = () => {
            hasFetched.current = false;
        };

        axios
            .get(`/api/v1/courses/${props.courseId}/membership_enrollment_process_flows`)
            .then((res) => setFlows(res.data.flows))
            .catch(onError);
        axios
            .get(`/api/v1/courses/${props.courseId}/membership_enrollment_documents`)
            .then((res) => {
                const documents = res.data.documents;
                setCorporateMemberDocuments(documents.filter((v) => v.target === 'corporation'));
                setIndividualDocuments(documents.filter((v) => v.target === 'individual'));
                setAllTargetDocuments(documents.filter((v) => v.target === 'all_target'));
            })
            .catch(onError);
        axios
            .get(`/api/v1/courses/${props.courseId}/materials`)
            .then((res) => setMaterials(res.data.materials))
            .catch(onError);
    }, [props.show]);

    const detailContent = (summary: string, content: string) => {
        if (content) return parseSanitizedHtml(`${summary}<br><br>${content}`);

        return summary;
    };

    // enum値を日本語に変換するヘルパー関数
    const getEnrollableLabel = (enrollableValue: string) => {
        const labels = {
            available: '可',
            consultation_required: '要相談',
            unavailable: '不可',
            unknown: '不明',
            free_text: '', // フリーワードの場合は空文字
        };
        return labels[enrollableValue] || '';
    };

    // enrollableの表示内容を取得
    const getEnrollableContent = (enrollableValue: string, freeText: string, additionalInfo?: string) => {
        if (enrollableValue === 'free_text') {
            return parseSanitizedHtml(freeText);
        }
        if (enrollableValue === 'available' && additionalInfo) {
            return parseSanitizedHtml(additionalInfo);
        }
        return getEnrollableLabel(enrollableValue);
    };

    return (
        <div className={`${s.container} ${props.show ? '' : tabPanel.tab_panel_hidden}`}>
            <Heading2 title={`${cleanCourseName(props.courseName)}の会員権購入価格`} />
            {props.children}
            <Heading2 title={`${cleanCourseName(props.courseName)}入会条件`} />
            <div className={s.head_2_body}>
                <table className={s.admission_condition_table}>
                    <tbody>
                        {props.course.femaleEnrollable && props.course.femaleEnrollable !== 'unknown' && (
                            <tr>
                                <th>女性入会</th>
                                <td>
                                    {getEnrollableContent(
                                        props.course.femaleEnrollable,
                                        props.course.femaleEnrollableFreeText
                                    )}
                                </td>
                            </tr>
                        )}
                        {props.course.foreignEnrollable && props.course.foreignEnrollable !== 'unknown' && (
                            <tr>
                                <th>外国籍入会</th>
                                <td>
                                    {getEnrollableContent(
                                        props.course.foreignEnrollable,
                                        props.course.foreignEnrollableFreeText,
                                        props.course.foreignEnrollmentInformation
                                    )}
                                </td>
                            </tr>
                        )}
                        {props.course.corporateEnrollable && props.course.corporateEnrollable !== 'unknown' && (
                            <tr>
                                <th>法人入会</th>
                                <td>
                                    {getEnrollableContent(
                                        props.course.corporateEnrollable,
                                        props.course.corporateEnrollableFreeText
                                    )}
                                </td>
                            </tr>
                        )}
                        {props.course.isNoAgeLimit !== null && (
                            <tr>
                                <th>年齢制限</th>
                                <td>
                                    {props.course.isNoAgeLimit ? 'なし' : detailContent('あり', props.course.ageLimit)}
                                </td>
                            </tr>
                        )}
                        {props.course.isIntroducerUnnecessary !== null && (
                            <tr>
                                <th>推薦人</th>
                                <td>
                                    {props.course.isIntroducerUnnecessary
                                        ? '不要'
                                        : detailContent('必要', props.course.introducerInformation)}
                                </td>
                            </tr>
                        )}
                        {props.course.isHandicapUnnecessary !== null && (
                            <tr>
                                <th>ハンディキャップ</th>
                                <td>{props.course.isHandicapUnnecessary ? '不要' : '必要'}</td>
                            </tr>
                        )}
                        {props.course.isEnrollmentInOthersUnnecessary !== null && (
                            <tr>
                                <th>他クラブの所属</th>
                                <td>
                                    {props.course.isEnrollmentInOthersUnnecessary
                                        ? '不要'
                                        : detailContent('必要', props.course.enrollmentInOthersInformation)}
                                </td>
                            </tr>
                        )}
                        {props.course.isInterviewUnnecessary !== null && (
                            <tr>
                                <th>面接</th>
                                <td>
                                    {props.course.isInterviewUnnecessary
                                        ? 'なし'
                                        : detailContent('あり', props.course.interviewInformation)}
                                </td>
                            </tr>
                        )}
                        {props.course.isAccompanyImpossible !== null && (
                            <tr>
                                <th>同伴プレー</th>
                                <td>{props.course.isAccompanyImpossible ? 'なし' : 'あり'}</td>
                            </tr>
                        )}
                        {props.course.reenrollmentInformation !== null && (
                            <tr>
                                <th>再入会</th>
                                <td>{parseSanitizedHtml(props.course.reenrollmentInformation)}</td>
                            </tr>
                        )}
                        {props.course.note !== null && (
                            <tr>
                                <th>備考</th>
                                <td>{parseSanitizedHtml(props.course.note)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {flows.length ? (
                <>
                    <Heading2 title={`${cleanCourseName(props.courseName)}入会の流れ`} />
                    <div className={s.head_2_body}>
                        {flows.map((flow) => (
                            <p className={s.buying_step} key={flow.order}>
                                {flow.order}. {flow.description}
                            </p>
                        ))}
                    </div>
                </>
            ) : (
                ''
            )}

            {displayDocuments ? (
                <>
                    <Heading2 title={`${cleanCourseName(props.courseName)}入会の必要書類`} />
                    <div className={s.head_2_body}>
                        <div className={s.document_without_h3}>
                            <UnorderedList items={allTargetDocuments} />
                        </div>
                        {individualDocuments.length > 0 && (
                            <>
                                <Heading3 title={'個人入会の場合に必要な書類'} />
                                <div className={s.head_3_body}>
                                    <UnorderedList items={individualDocuments} />
                                </div>
                            </>
                        )}
                        {corporateMemberDocuments.length > 0 && (
                            <>
                                <Heading3 title={'法人入会の場合に必要な書類'} />
                                <div className={s.head_3_body}>
                                    <UnorderedList items={corporateMemberDocuments} />
                                </div>
                            </>
                        )}
                    </div>
                </>
            ) : (
                ''
            )}

            {materials.length ? (
                <>
                    <Heading2 title={`${cleanCourseName(props.courseName)}入会に関する参考資料`} />
                    <div className={s.head_2_body}>
                        <div className={s.head_3_body}>
                            <UnorderedList items={materials} />
                        </div>
                    </div>
                </>
            ) : (
                ''
            )}
        </div>
    );
};

export default Buying;
